import { ulMessageCheckSquare } from '@faceup/icons/ulMessageCheckSquare'
import { ulPlus } from '@faceup/icons/ulPlus'
import { ulSearchSm } from '@faceup/icons/ulSearchSm'
import { defineMessages, FormattedMessage } from '@faceup/localization'
import { Institution, type Plan } from '@faceup/utils'
import { useContext } from 'react'
import type { MenuItem } from '../Components/MenuItems/MenuItems'
import { UserContext } from '../Contexts/UserContext'
import useAnalytics from '../utils/analytics'
import { useInstitution } from './useInstitution'
import { useIsCheckReportAvailable } from './useIsCheckReportAvailable'

const messages = defineMessages('followUp.hooks.useReportMenuItems', [
  { id: 'createReport', defaultMessage: 'Create a case' },
  { id: 'createReportForTestingSchool', defaultMessage: 'Create test case' },
  { id: 'backToSchoolList', defaultMessage: 'Back to searching for school' },
  { id: 'checkReport', defaultMessage: 'Check a case' },
] as const)

type UseReportMenuItemsParams = {
  plan: Plan | null | undefined
}

export const useReportMenuItems = ({ plan }: UseReportMenuItemsParams): MenuItem[] => {
  const { isCheckReportAvailable } = useIsCheckReportAvailable({ plan })
  const { logout } = useContext(UserContext)
  const institution = useInstitution()
  const { track } = useAnalytics()

  const isTestingSchool = institution?.isTesting === true && institution.type === Institution.School

  return [
    {
      key: 'createReport',
      url: routes => routes.createReport(),
      onClick: () => track('create_report_button_clicked'),
      icon: ulPlus,
      label: isTestingSchool ? (
        <FormattedMessage message={messages.createReportForTestingSchool} />
      ) : (
        <FormattedMessage message={messages.createReport} />
      ),
      variant: 'filled',
      dataTest: 'menu-item-create',
    },
    ...(isCheckReportAvailable
      ? [
          {
            key: 'checkReport',
            url: routes => routes.checkReport(),
            icon: ulMessageCheckSquare,
            label: <FormattedMessage message={messages.checkReport} />,
            dataTest: 'menu-item-check',
          } satisfies MenuItem,
        ]
      : []),
    ...(isTestingSchool
      ? [
          {
            key: 'logout',
            onClick: logout,
            icon: ulSearchSm,
            label: <FormattedMessage message={messages.backToSchoolList} />,
          } satisfies MenuItem,
        ]
      : []),
  ]
}
